import axios from "axios";
import { API_BASE_PATHS, API_ROUTES } from "../libs";
import { buildRequestConfig } from "./buildRequestConfig";
import { AddReplyPayload, AddReplyResponse, CreateTicketRequestDto, Ticket, TicketRepliesResponse, TicketResponse, UpdateTicketStatusAdminPayload } from "../libs/models/ticket";

class supportService {
    async getAllTicketPriorities() {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${API_ROUTES.GET_PRIORITIES}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.log("There is an error while fetching all media items", err);
            throw new Error(err);
        }
    }
    async getAllTicketStatuses() {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${API_ROUTES.GET_STATUSES}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.log("There is an error while fetching all media items", err);
            throw new Error(err);
        }
    }

    async getAllTicketTypes() {
        try {
            const config = await buildRequestConfig();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${API_ROUTES.GET_TYPES}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.log("There is an error while fetching all media items", err);
            throw new Error(err);
        }
    }

    async getAllTickets(queryParams = {}) {
        try {
            const config = await buildRequestConfig();

            // Filter out empty query parameters
            const filteredParams = Object.fromEntries(
                Object.entries(queryParams).filter(([_, value]) => value)
            ) as Record<string, string>; 

            const queryString = new URLSearchParams(filteredParams).toString();
            const response = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}?${queryString}`,
                { ...config }
            );

            return response.data.data;
        } catch (err: any) {
            console.error("Error fetching tickets", err);
            throw new Error(err);
        }
    }

    async createTickets(payload: CreateTicketRequestDto): Promise<Ticket> {
        try {
            const config = await buildRequestConfig();

            const response = await axios.post(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}`,
                { ...payload },
                { ...config }
            );

            return response.data as Ticket;  
        } catch (err: any) {
            console.error("There was an error while creating the ticket:", err);
            throw new Error(err);
        }
    }


    async getSingleByTicketId(ticketId: string): Promise<Ticket> {
        try {
            const config = await buildRequestConfig();
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${ticketId}`,
                { ...config }
            );

            // ✅ Ensure the full ticket object is returned
            return data.data as Ticket;
        } catch (err: any) {
            console.error("Error fetching ticket:", err);
            const errorMessage =
                err.response?.data?.error?.message ??
                (typeof err.response?.data?.error === "string" ? err.response.data.error : "An unexpected error occurred.");

            throw new Error(errorMessage);
        }
    }

    async updateSingleByTicketId(ticketId: string, payload: UpdateTicketStatusAdminPayload): Promise<Ticket> {
        try {
            const config = await buildRequestConfig();

            // ✅ Use PUT request with payload
            const { data } = await axios.put(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${ticketId}`,
                payload,   // Send the payload
                { ...config }
            );

            // ✅ Return the full updated ticket object
            return data.data as Ticket;
        } catch (err: any) {
            console.error("Error updating ticket:", err);
            const errorMessage =
                err.response?.data?.error?.message ??
                (typeof err.response?.data?.error === "string"
                    ? err.response.data.error
                    : "An unexpected error occurred.");

            throw new Error(errorMessage);
        }
    }

    async getRepliesByTicketId(ticketId: string): Promise<TicketRepliesResponse["data"]> {
        try {
            const config = await buildRequestConfig();
            const { data: responseData } = await axios.get<TicketRepliesResponse>(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${ticketId}/${API_ROUTES.GET_REPLIES}`,
                { ...config }
            );
            return responseData.data;
        } catch (err: any) {
            console.error("Error fetching replies:", err);
            const errorMessage =
                err.response?.data?.error?.message ??
                (typeof err.response?.data?.error === "string" ? err.response.data.error : "An unexpected error occurred.");

            throw new Error(errorMessage);
        }
    }

    async addReplyToTicket(
        ticketId?: string,
        payload?: AddReplyPayload
    ): Promise<AddReplyResponse> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}/${ticketId}/${API_ROUTES.ADD_REPLY}`,
                { ...payload },
                { ...config }
            );

            return response.data as AddReplyResponse;
        } catch (err: any) {
            console.log("There was an error while adding the reply:", err);
            throw new Error(err);
        }
    }
    async createTicket(ticketData: Ticket): Promise<TicketResponse> {
        try {
            const config = await buildRequestConfig();
            const response = await axios.post(
                `${process.env.REACT_APP_API_HOST}/${API_BASE_PATHS.SUPPORT_API}/${API_ROUTES.STATIC_TICKET}`,
                ticketData,
                { ...config }
            );
    
            // ✅ Return the entire backend response
            return response.data as TicketResponse;
    
        } catch (err: any) {
            console.error("Error while creating a support ticket:", err);
    
            // ✅ Include error message in the response
            return {
                data: {} as Ticket,   // Empty ticket object in case of failure
                error: {
                    message: err.response?.data?.error?.message || "An unexpected error occurred."
                }
            };
        }
    }
    
}

export const SupportService = new supportService();